import { ResponsiveImage } from '../ResponsiveImage';
import type { SanityImage } from '@app/typeStore';
import { stegaClean } from '@sanity/client/stega';
import type { SVGProps } from 'react';
import { tv } from 'tailwind-variants';
import { toKebabCase } from '../_lib/toKebabCase';
import { cn } from '../_lib/utils';

export type IconType = string | SanityImage;

export function IconResolver(
	props: SVGProps<SVGSVGElement> & {
		icon?: IconType;
		className?: string;
	},
) {
	const { icon, className, ...attr } = props;

	if (!icon) return null;

	return (
		<>
			{typeof icon === 'string' ? (
				<IconSvgComponent name={stegaClean(icon)} attr={attr} className={className} />
			) : (
				<ResponsiveImage
					image={icon as SanityImage}
					objectFit='contain'
					className={cn('w-full h-full flex items-center justify-center', className)}
				/>
			)}
		</>
	);
}

type IconSvgComponentProps = {
	name: string;
	className?: string;
	attr?: SVGProps<SVGSVGElement>;
};

const styles = tv({
	slots: {
		base: 'block w-6 h-6',
	},
});

function IconSvgComponent({ name, attr, className }: IconSvgComponentProps) {
	if (!name) {
		return null;
	}
	const code = toKebabCase(name) + '-bold';
	const { base } = styles();

	if (!code) {
		console.error('Icon code is required');
		return null;
	}
	return (
		<svg width='100%' height='100%' viewBox={'0 0 24 24'} className={base({ className })} {...attr} fill={'currentColor'}>
			<title>{convertToTitleCase(code.replace('-bold', ''))}</title>
			<use href={`/icons/ph/${code}.svg#icon`} />
		</svg>
	);
}

/**
 * Converts a string to title case
 */
function convertToTitleCase(str: string): string {
	return str.replace(/[-_]/g, ' ').replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
}
